/* eslint-disable react/state-in-constructor */

import { EmptyState } from '@lmc/ui-kit'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import { Outlet } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../actions'
import { getNominative } from '../../common/utils'
import { AccessRestriction, Spinner } from '../../components/generic'
import Operations from '../../operations'
import Selectors from '../../selectors'
import { history } from '../../store'
import { withRouter } from '../../withRouter'
import { ResidentsSidebarNavigation } from './components'
import { getResidentSideNavItems } from './utils'

export class ResidentsScreen extends Component {
  state = {
    isSubNav: false,
    hasGPConnectEnabled: false
  }

  componentDidMount() {
    const { features, fetchZones } = this.props
    if (features?.pro) {
      fetchZones()
    }
  }

  componentDidUpdate() {
    const showNavigationSidebar =
      !isDesktop &&
      this.state.isSubNav &&
      (history.action === 'REPLACE' || history.action === 'POP') &&
      (history.location.pathname.endsWith('/profile') ||
        history.location.pathname.endsWith('/care-plans'))

    const navigationExeptions =
      !isDesktop &&
      !this.state.isSubNav &&
      history.location.pathname.endsWith('/daily-report')

    if (showNavigationSidebar) {
      this.setState({ isSubNav: false })
    }
    if (navigationExeptions) {
      this.setState({ isSubNav: true })
    }
    if (history.location.pathname === '/residents') {
      history.replace('residents/list')
    }
  }

  renderDesktop = () => {
    const {
      allResidents,
      residents,
      router,
      user,
      carers,
      preferredPeopleNominative,
      features,
      hasGPConnectEnabled
    } = this.props

    const resident = _.find(residents, {
      id: router?.params?.id
    })

    const { isSubNav } = this.state

    const showSidebar = isSubNav === false || isBrowser

    const showChildScreens = isSubNav || isBrowser

    const getGPConnectPermission = () =>
      carers?.find((carer) => carer.id === user?.id)?.gpConnectPermission

    const hasGPConnectClinicalAccessUser = getGPConnectPermission()
    const hasRiskAssessment = features?.features?.pro?.enabled
    const hasGoals = features?.features?.goals?.enabled
    const hasSharedCareRecord = features?.features?.cerner?.enabled
    const hasRota = features?.features?.rota?.enabled

    const menuItems = getResidentSideNavItems({
      ...this.props,
      hasGPConnectClinicalAccessUser,
      hasRiskAssessment,
      hasSharedCareRecord,
      hasGoals,
      hasRota,
      hasGPConnectEnabled,
      communicationRecordsEnabled: this.props.communicationRecordsEnabled,
      proceduresEnabled: this.props.proceduresEnabled,
      emarChartV2Enabled: this.props.emarChartV2Enabled,
      hasSharedCareRecordPermission: this.props.hasSharedCareRecordPermission,
      user,
      resident
    })

    // An empty list means it is still loading, not that the id is unknown.
    if (!resident && residents?.length) {
      history.push(`/residents/list`)
    }

    return (
      <div style={styles.mainContainer} data-testid="residents-screen">
        {showSidebar && (
          <ResidentsSidebarNavigation
            menuItems={menuItems}
            preferredPeopleNominative={preferredPeopleNominative}
            resident={resident}
            navigate={(key) => {
              this.setState({ isSubNav: true })
              history.push(`/residents/${key}`)
            }}
          />
        )}
        {showChildScreens && (
          <StyleRightContainer data-testid="residents-content-container">
            {!allResidents.length ? (
              <EmptyState
                title={getNoResidentsMessage(preferredPeopleNominative)}
                description={
                  user.role === 'senior-carer'
                    ? getSupportSummarySeniors(preferredPeopleNominative)
                    : getSupportSummary(preferredPeopleNominative)
                }
                data-testid="residents-empty-state"
              >
                <AccessRestriction>
                  <a
                    href={SUPPORT_ARTICLE}
                    rel="noreferrer"
                    style={{ paddingLeft: 5 }}
                    target="_blank"
                    data-testid="support-article-link"
                  >
                    Find out more
                  </a>
                </AccessRestriction>
              </EmptyState>
            ) : (
              // Remount the per-resident subtree on resident switch so stale local
              // component state (tabs, filters, in-progress forms) doesn't survive
              // into the newly selected resident. Sidebar sits outside this key.
              <Outlet key={router?.params?.id} />
            )}
          </StyleRightContainer>
        )}
      </div>
    )
  }

  render() {
    const { allResidents, features, filters, residents } = this.props
    if (!allResidents || !features || !filters || !residents) {
      return <Spinner />
    }

    return this.renderDesktop()
  }
}

const getNoResidentsMessage = (preferredPeopleNominative) =>
  `You haven't added any ${getNominative(
    preferredPeopleNominative,
    'residents'
  )} yet`
const getSupportSummarySeniors = (preferredPeopleNominative) =>
  `Admins can add ${getNominative(
    preferredPeopleNominative,
    'residents'
  )} to view their information in the Carer App and begin logging care.`
const getSupportSummary = (preferredPeopleNominative) =>
  `Add ${getNominative(
    preferredPeopleNominative,
    'residents'
  )} to view their information in the Carer App and begin logging care.`
const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/the-care-office/get-started-in-minutes/how-do-i-add-a-new-resident'

const styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    margin: '0 auto',
    height: 'calc(100vh - 65px)',
    maxWidth: 2000,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  rightContainer: {
    wordWrap: 'break-word',
    width: '100%',
    marginLeft: 24,
    overflowY: 'auto'
  },
  noResidentsMessage: {
    padding: 60,
    marginTop: 50,
    maxWidth: 1000
  }
}

const StyleRightContainer = styled.div`
  word-wrap: break-word;
  width: 100%;
  margin-left: 24px;
  padding-right: 24px;
  overflow-y: auto;
`

const isDesktop = isBrowser || isTablet

ResidentsScreen.propTypes = {
  features: PropTypes.object,
  fetchZones: PropTypes.func.isRequired,
  residents: PropTypes.array,
  user: PropTypes.object,
  home: PropTypes.object,
  alertsEnabled: PropTypes.bool,
  communicationRecordsEnabled: PropTypes.bool,
  proceduresEnabled: PropTypes.bool,
  referralsEnabled: PropTypes.bool,
  serviceRecordsEnabled: PropTypes.bool,
  deviceRecordsEnabled: PropTypes.bool,
  emarV2StockEnabled: PropTypes.bool
}

const mapStateToProps = (state) => ({
  allResidents: state.data.residents,
  filters: Selectors.getResidentFilters(state),
  features: state.global.home,
  residents: Selectors.sortFilteredResidents(state),
  user: state.global.user,
  carers: state.data.carers,
  home: state.global.home,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  hasGPConnectEnabled:
    !!state.global.home.odsCode && !!state.global.home.asidCode,
  hasSharedCareRecordPermission:
    state.global.ability?.can?.('view', 'SharedCareRecord') ?? false
})

const mapDispatchToProps = (dispatch) => ({
  fetchZones: () => {
    dispatch(Operations.loadList('zones'))
  },
  openModal: () => {
    const id = 'addResident'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ResidentsScreen))
